import axios from 'axios';
import toast from 'react-hot-toast';
import {
  languageToAcceptLanguage,
  toastMessageKey,
} from '@constants/languages';
import i18n from '../i18n';

const messages = {
  product: {
    updated: {
      en: 'Product updated successfully',
      es: 'Producto actualizado exitosamente',
    },
    enabled: {
      en: 'Product enabled successfully',
      es: 'Producto habilitado exitosamente',
    },
    disabled: {
      en: 'Product disabled successfully',
      es: 'Producto deshabilitado exitosamente',
    },
    removed: {
      en: 'Product removed successfully',
      es: 'Producto eliminado exitosamente',
    },
  },
  purchase: {
    updated: {
      en: 'Purchase updated successfully',
      es: 'Compra actualizada exitosamente',
    },
    accepted: {
      en: 'Purchase accepted successfully',
      es: 'Compra aceptada exitosamente',
    },
    accepted_invalid: {
      en: 'Purchase cannot be accepted, out of stock',
      es: 'La compra no puede ser aceptada, fuera de stock',
    },
    rejected: {
      en: 'Purchase rejected successfully',
      es: 'Compra rechazada exitosamente',
    },
    bulkAccepted: {
      en: 'Purchases accepted successfully',
      es: 'Compras aceptadas exitosamente',
    },
    bulkAccepted_invalid: {
      en: 'Purchases cannot be accepted, out of stock',
      es: 'Las compras no pueden ser aceptadas, fuera de stock',
    },
    bulkRejected: {
      en: 'Purchases rejected successfully',
      es: 'Compras rechazadas exitosamente',
    },
  },
  loyalty: {
    accepted: {
      en: 'Loyalty points accepted successfully',
      es: 'Puntos de lealtad aceptados exitosamente',
    },
    rejected: {
      en: 'Loyalty points rejected successfully',
      es: 'Puntos de lealtad rechazados exitosamente',
    },
  },
  bonus: {
    removed: {
      en: 'Bonus removed successfully',
      es: 'Bono eliminado exitosamente',
    },
  },
  rewards: {
    updated: {
      en: 'Successfully Updated Value',
      es: 'Valor actualizado exitosamente',
    },
    removed: {
      en: 'Rewards removed successfully',
      es: 'Recompensas eliminadas exitosamente',
    },
    accepted: {
      en: 'Reward accepted successfully',
      es: 'Recompensa aceptada exitosamente',
    },
    rejected: {
      en: 'Reward rejected successfully',
      es: 'Recompensa rechazada exitosamente',
    },
    bulkAccepted: {
      en: 'Rewards accepted successfully',
      es: 'Recompensas aceptadas exitosamente',
    },
    bulkRejected: {
      en: 'Rewards rejected successfully',
      es: 'Recompensas rechazadas exitosamente',
    },
  },
  gifts: {
    accepted: {
      en: 'Gift accepted successfully',
      es: 'Regalo aceptado exitosamente',
    },
    rejected: {
      en: 'Gift rejected successfully',
      es: 'Regalo rechazado exitosamente',
    },
    bulkAccepted: {
      en: 'Gifts accepted successfully',
      es: 'Regalos aceptados exitosamente',
    },
    bulkRejected: {
      en: 'Gifts rejected successfully',
      es: 'Regalos rechazados exitosamente',
    },
  },
};

const getMessage = (category, type) => {
  try {
    const currentLang = toastMessageKey(i18n.language);
    return messages[category][type][currentLang];
  } catch (error) {
    console.warn(`Message not found for ${category}.${type}`);
    return `${category} ${type} successfully`;
  }
};

export const showPurchaseStatusChangeSuccessToast = accepted => {
  toast.success(getMessage('purchase', accepted ? 'accepted' : 'rejected'));
};

export const showBulkPurchaseResultToasts = (result, accepted) => {
  const updated = result?.updated_count ?? 0;
  const skipped = result?.skipped_count ?? 0;
  const failed = result?.failed_count ?? 0;

  if (updated > 0) {
    toast.success(
      i18n.t(
        accepted ? 'approve.bulkPurchasesApproved' : 'approve.bulkPurchasesRejected',
        {count: updated},
      ),
    );
  }
  if (skipped > 0) {
    toast(i18n.t('approve.bulkPurchasesSkipped', {count: skipped}), {
      icon: 'ℹ️',
    });
  }
  if (failed > 0) {
    toast.error(i18n.t('approve.bulkPurchasesFailed', {count: failed}));
  }
  if (updated === 0 && skipped === 0 && failed === 0) {
    toast.error(i18n.t('approve.bulkPurchasesNoChanges'));
  }
};

const getApiHost = () => {
  const hostname = window.location.hostname;

  if (hostname === 'store.nextleveltechapp.com') {
    return 'https://store.nextleveltechapp.com'; // Production
  }

  if (
    hostname === 'next-level-services-24-staging.botics.co' ||
    hostname === 'next-level-services-24-staging.azurewebsites.net'
  ) {
    return 'https://next-level-services-24-staging.azurewebsites.net'; // Staging
  }

  if (hostname === 'localhost') {
    return 'http://localhost:8000'; // Local
  }

  return 'https://store.nextleveltechapp.com'; // Default fallback (prod)
};

export const API_HOST = getApiHost();

const getLanguageHeader = () => {
  return languageToAcceptLanguage(i18n.language);
};

const getBaseHeaders = (hasFile = false) => {
  const token =
    localStorage.getItem('nls:auth') || sessionStorage.getItem('nls:auth');
  const headers = {
    'Accept-Language': getLanguageHeader(),
  };

  if (!hasFile) {
    headers.Accept = 'application/json, text/plain, */*';
    headers['Content-Type'] = 'application/json';
  }

  if (token) {
    headers.Authorization = `Token ${token}`;
  }

  return headers;
};

export const doFetch = async (url, params, hasFile = false) => {
  const headers = getBaseHeaders(hasFile);

  return fetch(API_HOST + url, {
    ...params,
    headers: {
      ...headers,
      ...(params?.headers || {}),
    },
  }).then(async response => {
    if (response.ok) {
      // Check if response has content before parsing as JSON
      const contentType = response.headers.get('content-type');
      const contentLength = response.headers.get('content-length');

      // If no content or empty response, return success without parsing
      if (contentLength === '0' || !contentType?.includes('application/json')) {
        // For successful responses without JSON content (like file uploads)
        const text = await response.text();
        return text || {success: true};
      }

      // Try to parse as JSON, but handle empty responses gracefully
      try {
        const data = await response.json();
        return data;
      } catch (jsonError) {
        // If JSON parsing fails but response was successful, return success
        console.warn('Response was successful but not valid JSON:', jsonError);
        return {success: true};
      }
    } else {
      const errorResponseText = await response.text();
      let parsedError;
      try {
        parsedError = JSON.parse(errorResponseText);
      } catch (e) {
        // If parsing fails, it might be a plain text error or HTML error page
        return Promise.reject(errorResponseText);
      }

      const specificErrorMessage = await handleError(parsedError);

      if (
        specificErrorMessage &&
        specificErrorMessage.includes('Invalid token.')
      ) {
        // Special handling for token errors, perhaps to trigger a logout or re-auth
        // Depending on the app's structure, you might want to navigate or clear auth state here.
        // For now, we'll still reject so the calling mutation can be aware it's an auth issue.
        // return; // Or handle differently if it shouldn't be treated as a rejectable error by the mutation
      }

      // For all other errors, including validation errors from signup, reject with the specific message
      return Promise.reject(specificErrorMessage);
    }
  });
};

export const handleError = error => {
  if (error.detail) {
    return Array.isArray(error.detail) ? error.detail[0] : error.detail;
  }
  const errorKeys = Object.keys(error);
  if (errorKeys.includes('non_field_errors')) {
    return error.non_field_errors && error.non_field_errors[0];
  }
  if (errorKeys.includes('email')) {
    return error.email[0] || error.email.email[0];
  }
  const firstKey = errorKeys[0];
  const value = error[firstKey];
  return Array.isArray(value) ? value[0] : `${value}`;
};

export const loginApi = async params => {
  let data = await doFetch('/api/v1/rest-auth/login/', {
    method: 'POST',
    body: JSON.stringify(params),
  });

  return data;
};

export const registerApi = params => {
  return doFetch('/admin-api/v1/auth/sign-up/', {
    method: 'POST',
    body: JSON.stringify(params),
  });
};

export const forgotPasswordApi = params => {
  return doFetch('/admin-api/v1/auth/forgot-password/', {
    method: 'POST',
    body: JSON.stringify(params),
  });
};

export const resetPasswordApi = params => {
  return doFetch('/admin-api/v1/auth/reset-password/', {
    method: 'POST',
    body: JSON.stringify(params),
  });
};
export const addProductApi = params => {
  return doFetch('/admin-api/v1/products/', {
    method: 'POST',
    body: JSON.stringify(params),
  });
};
export const ImgUploadtApi = params => {
  return doFetch(
    '/admin-api/v1/products/' + params.id + '/add-image/',
    {
      method: 'POST',
      body: params.formdata,
    },
    true,
  );
};

export const Acceptapi = params => {
  return doFetch('/admin-api/v1/earned-rewards/' + params.id + '/', {
    method: 'PATCH',
    body: JSON.stringify({accepted: params.accept}),
  });
};

export const ProductPurchaseNew = (id, params) => {
  return doFetch(`/admin-api/v1/product-purchases/${id}/status-change/`, {
    method: 'POST',
    body: JSON.stringify(params),
  });
};

export const UpdateProductApi = params => {
  return doFetch('/admin-api/v1/products/' + params.id + '/', {
    method: 'PATCH',
    body: JSON.stringify({
      name_en: params.name_en,
      name_es_mx: params.name_es_mx,
      points_cost: params.points_cost,
      available_stock: params.available_stock,
      description_en: params.description_en,
      description_es_mx: params.description_es_mx,
      enabled: params.enabled,
      is_gift: params.is_gift,
    }),
  }).then(data => {
    toast.success(getMessage('product', 'updated'));
    return data;
  });
};
export const PATCHUpdateProductApi = params => {
  return doFetch('/admin-api/v1/products/' + params.id + '/', {
    method: 'PATCH',
    body: JSON.stringify({
      name_en: params.name_en ?? params.name,
      name_es_mx: params.name_es_mx,
      points_cost: params.points_cost,
      available_stock: params.available_stock,
      description_en: params.description_en ?? params.description,
      description_es_mx: params.description_es_mx,
      enabled: params.enabled,
      is_gift: params.is_gift,
    }),
  })
    .then(() => {
      toast.success(
        getMessage('product', params.enabled ? 'enabled' : 'disabled'),
      );
    })
    .catch(e => {
      toast.dismiss();
      console.log('error award save', e);
    });
};
export const PATCHUpdateRewardApi = params => {
  return doFetch('/admin-api/v1/level-awards/' + params.id + '/', {
    method: 'PATCH',
    body: JSON.stringify({
      award_type_en: params.award_type_en,
      discount: params.discount,
      points: params.points,
      level: params.level,
      enabled: params.enabled,
    }),
  });
};
export const addMountSpentApi = params => {
  return doFetch('/admin-api/v1/shop-users/' + params.id + '/money-spents/', {
    method: 'POST',
    body: JSON.stringify({amount: params.amount, note: params.note}),
  });
};
export const giveBonusesApi = params => {
  return doFetch(
    '/admin-api/v1/shop-users/' + params.id + '/money-spent-bonuses/',
    {
      method: 'POST',
      body: JSON.stringify({
        money_spent: params.money_spent,
        discount: params.discount,
        points: params.points,
        reward_type: params.gift,
      }),
    },
  );
};
export const delProductApi = params => {
  return doFetch('/admin-api/v1/products/' + params + '/', {
    method: 'DELETE',
  })
    .then(() => {
      toast.success(getMessage('product', 'removed'));
    })
    .catch(e => {
      toast.dismiss();
      console.log('error award save', e);
    });
};
export const delShopUserApi = params => {
  return doFetch('/admin-api/v1/shop-users/' + params + '/', {
    method: 'DELETE',
  });
};
export const delBounusesApi = params => {
  return doFetch('/admin-api/v1/bonuses/' + params + '/', {
    method: 'DELETE',
  })
    .then(() => {
      toast.success(getMessage('bonus', 'removed'));
    })
    .catch(e => {
      toast.dismiss();
      console.log('error award save', e);
    });
};
export const delRewardApi = params => {
  return doFetch('/admin-api/v1/level-awards/' + params + '/', {
    method: 'DELETE',
  })
    .then(() => {
      toast.success(getMessage('rewards', 'removed'));
    })
    .catch(e => {
      toast.dismiss();
      console.log('error award save', e);
    });
};
export const getStoreApi = () => {
  return doFetch('/admin-api/v1/stores/self/');
};

export const updateStoreApi = params => {
  const {
    name,
    logo,
    points_expire,
    money_spent_bonus,
    automate_earned_rewards,
  } = params;

  const token =
    localStorage.getItem('nls:auth') || sessionStorage.getItem('nls:auth');

  // If we have a logo file, use FormData (multipart)
  if (logo && typeof logo === 'object' && logo.constructor === File) {
    const formData = new FormData();

    // Only append fields that are provided
    if (name !== undefined) formData.append('name', name);
    if (logo) formData.append('logo', logo);
    if (points_expire !== undefined)
      formData.append('points_expire', points_expire);
    if (money_spent_bonus !== undefined)
      formData.append('money_spent_bonus', money_spent_bonus);
    if (automate_earned_rewards !== undefined)
      formData.append('automate_earned_rewards', automate_earned_rewards);

    const config = {
      method: 'patch',
      url: API_HOST + '/admin-api/v1/stores/self/',
      headers: {
        Authorization: 'token ' + token,
        // Don't set Content-Type when using FormData - let axios handle it
      },
      data: formData,
    };

    return axios(config)
      .then(function(response) {
        return response.data;
      })
      .catch(function(error) {
        console.log(error);
        throw error;
      });
  } else {
    // For text-only updates, use JSON
    const updateData = {};

    if (name !== undefined) updateData.name = name;
    if (points_expire !== undefined) updateData.points_expire = points_expire;
    if (money_spent_bonus !== undefined)
      updateData.money_spent_bonus = money_spent_bonus;
    if (automate_earned_rewards !== undefined)
      updateData.automate_earned_rewards = automate_earned_rewards;

    const config = {
      method: 'patch',
      url: API_HOST + '/admin-api/v1/stores/self/',
      headers: {
        Authorization: 'token ' + token,
        'Content-Type': 'application/json',
      },
      data: updateData,
    };

    return axios(config)
      .then(function(response) {
        return response.data;
      })
      .catch(function(error) {
        console.log(error);
        throw error;
      });
  }
};

export const toggleUserActiveStatus = id => {
  return doFetch('/admin-api/v1/toggle-user-active-status/', {
    method: 'POST',
    body: JSON.stringify({
      id: id,
    }),
  });
};

export const searchUsers = query => {
  return doFetch(`/admin-api/v1/shop-users/?q=${query}`);
};

export const getShopUsers = () => {
  return doFetch('/admin-api/v1/shop-users/');
};

export const findUsers = () => {
  return doFetch('/admin-api/v1/shop-users/');
};

export const AddBonusesApi = params => {
  return doFetch('/admin-api/v1/bonuses/', {
    method: 'POST',
    body: JSON.stringify({
      amount: params.amount,
      enabled: params.enabled,
      discount: params.discount,
      points: params.points,
      gift: params.gift,
    }),
  });
};
export const statusBonusesApi = params => {
  return doFetch('/admin-api/v1/bonuses/' + params.id + '/', {
    method: 'PATCH',
    body: JSON.stringify({
      amount: params.amount,
      enabled: params.enabled,
      discount: params.discount,
      points: params.points,
      gift: params.gift,
    }),
  });
};
export const getbonuses = () => {
  return doFetch('/admin-api/v1/bonuses/');
};
export const getreedemed = () => {
  return doFetch('/admin-api/v1/stores/stats-summary/');
};
export const awardManage = () => {
  return doFetch('/admin-api/v1/loyalty-levels/', {
    headers: {
      'Accept-Language': languageToAcceptLanguage(i18n.language),
    },
  });
};
export const awardSave = params => {
  return doFetch('/admin-api/v1/loyalty-levels/' + params.id + '/', {
    method: 'PUT',
    body: JSON.stringify(params),
  })
    .then(() => {
      toast.dismiss();
      toast.success(getMessage('rewards', 'updated'));
      awardManage();
    })
    .catch(e => {
      toast.dismiss();
      console.log('error award save', e);
    });
};
export const getReward = () => {
  // return doFetch('/admin-api/v1/earned-rewards/');
  return doFetch('/admin-api/v1/rewards/9');
};

const _approvalListQuery = (params, status) => {
  const page = params?.page ?? '';
  const pageSize = params?.page_size ?? '';
  const q =
    params?.q && String(params.q).trim()
      ? `&q=${encodeURIComponent(String(params.q).trim())}`
      : '';
  return `page=${page}&page_size=${pageSize}&status=${status}${q}`;
};

export const getRewardNewApi = async (params, status) => {
  return doFetch(`/admin-api/v1/rewards/?${_approvalListQuery(params, status)}`);
};

export const getGiftData = async (params, status) => {
  return doFetch(`/admin-api/v1/gifts/?${_approvalListQuery(params, status)}`);
};

export const getLatestTransaction = async () => {
  let data = await doFetch('/api/v1/transactions/');

  return data;
};

export const changeRewardStatus = async (id, params) => {
  return doFetch(`/admin-api/v1/rewards/${id}/reward_status_change/`, {
    method: 'POST',
    body: JSON.stringify(params),
  })
    .then(() => {
      toast.success(
        getMessage(
          'rewards',
          params.status === 'accepted' ? 'accepted' : 'rejected',
        ),
      );
    })
    .catch(e => {
      toast.dismiss();
      console.log('error award save', e);
    });
};

export const changeGiftStatus = async (id, params) => {
  return doFetch(`/admin-api/v1/gifts/${id}/gift_status_change/`, {
    method: 'POST',
    body: JSON.stringify(params),
  })
    .then(() => {
      toast.success(
        getMessage(
          'gifts',
          params.status === 'accepted' ? 'accepted' : 'rejected',
        ),
      );
    })
    .catch(e => {
      toast.dismiss();
      console.log('error award save', e);
    });
};

export const getPurchases = (params, status) => {
  return doFetch(
    `/admin-api/v1/product-purchases/?${_approvalListQuery(params, status)}`,
  );
};
export const getShopUsersDetail = params => {
  return doFetch('/admin-api/v1/shop-users/' + params, {
    method: 'GET',
  });
};
export const getProductReview = params => {
  return doFetch('/admin-api/v1/products/' + params + '/reviews/', {
    method: 'GET',
  });
};

export const removeReview = id => {
  return doFetch(`/admin-api/v1/product-review/${id}/delete`, {
    method: 'DELETE',
  });
};

export const getSingleUserPointList = id => {
  return doFetch('/shop-users/' + id + '/reward-list/', {
    method: 'GET',
  });
};

export const getProductReviewNew = params => {
  return doFetch('/admin-api/v1/products/' + params + '/reviews/', {
    method: 'GET',
  });
};

export const addOrUpdateProductReview = (params, id) => {
  return doFetch('/admin-api/v1/products/' + id + '/reviews/', {
    method: 'POST',
    body: JSON.stringify(params),
  });
};
export const getProductDetail = params => {
  return doFetch('/admin-api/v1/products/' + params + '/', {
    method: 'GET',
  });
};
export const getProductImages = productId => {
  return doFetch(`/admin-api/v1/products/${productId}/images/`, {
    method: 'GET',
  });
};

export const deleteProductImage = (productId, imageId) => {
  return doFetch(`/admin-api/v1/products/${productId}/images/${imageId}/`, {
    method: 'DELETE',
  });
};

export const getShopUsersPurchases = params => {
  return doFetch('/admin-api/v1/shop-users/' + params + '/product-purchases/', {
    method: 'GET',
  });
};
export const getShopUsersMoneySpents = params => {
  return doFetch('/admin-api/v1/shop-users/' + params + '/money-spents/', {
    method: 'GET',
  });
};
export const getShopUsersEarned = params => {
  return doFetch('/admin-api/v1/shop-users/' + params + '/earned-rewards/', {
    method: 'GET',
  });
};
export const getShopUsersGifts = params => {
  return doFetch('/admin-api/v1/shop-users/' + params + '/gifts/', {
    method: 'GET',
  });
};
export const getShopUsersMoneySpentsBonus = params => {
  return doFetch(
    '/admin-api/v1/shop-users/' + params + '/money-spent-bonuses/',
    {
      method: 'GET',
    },
  );
};
export const getProducts = () => {
  return doFetch('/admin-api/v1/products/');
};

export const getProductsWithPagination = (pageNumber = 1, searchQuery = '') => {
  const q = searchQuery ? `&q=${encodeURIComponent(searchQuery)}` : '';
  return doFetch(`/admin-api/v1/products/?page=${pageNumber}${q}`);
};

export const sendNotification = params => {
  return doFetch('/admin-api/v1/send-notifications/', {
    method: 'POST',
    body: JSON.stringify(params),
  });
};
export const bulkApprove = params => {
  return doFetch('/admin-api/v1/rewards/bulk-reward-status-change/', {
    method: 'POST',
    body: JSON.stringify({
      reward_ids: params.reward_ids,
      status: params.status,
    }),
  })
    .then(() => {
      toast.success(
        getMessage(
          'rewards',
          params.status === 'accepted' ? 'bulkAccepted' : 'bulkRejected',
        ),
      );
    })
    .catch(e => {
      toast.dismiss();
      console.log('error award save', e);
    });
};
export const bulkApprovePurchase = params => {
  return doFetch(
    '/admin-api/v1/product-purchases/bulk-product-purchase-status-change/',
    {
      method: 'POST',
      body: JSON.stringify({
        purchase_ids: params.purchase_ids,
        accepted: params.accepted,
      }),
    },
  );
};
export const bulkApproveGift = params => {
  return doFetch('/admin-api/v1/gifts/bulk-gift-status-change/', {
    method: 'POST',
    body: JSON.stringify({
      gift_ids: params.gift_ids,
      status: params.status,
    }),
  })
    .then(() => {
      toast.success(
        getMessage(
          'gifts',
          params.status === 'accepted' ? 'bulkAccepted' : 'bulkRejected',
        ),
      );
    })
    .catch(e => {
      toast.dismiss();
      console.log('error award save', e);
    });
};

export const downloadStoreQRCode = storeId => {
  return fetch(API_HOST + `/api/v1/stores/${storeId}/download-qr/`, {
    method: 'GET',
    headers: getBaseHeaders(),
  });
};
